import { Ingredient, Tag, TagImplication, TagUpdate, TagWithUsage } from './types.js';
import { authService } from '../auth/AuthService.js';

/** Every registered tag, alphabetical — for the tag picker's autocomplete. */
export async function listTags(): Promise<Tag[]> {
    const response = await fetch('/api/tags');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Every tag plus its ingredient-usage count — for the tags explorer's table. */
export async function listTagsWithUsage(): Promise<TagWithUsage[]> {
    const response = await fetch('/api/tags/usage');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Get-or-create a tag by name — see WriteService.get_or_create_tag(). The
 *  color only applies when actually creating; an existing match by name
 *  keeps its own color. */
export async function createOrGetTag(name: string, color?: string): Promise<Tag> {
    const response = await fetch('/api/tag/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(color ? { name, color } : { name }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to create tag' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Rename and/or recolor a tag. */
export async function updateTag(tagId: string, tagUpdate: TagUpdate): Promise<Tag> {
    const response = await fetch(`/api/tag/${tagId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(tagUpdate),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update tag' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Delete a tag entirely. Every ingredient that had it keeps its own data —
 *  only the association to this tag disappears. */
export async function deleteTag(tagId: string): Promise<void> {
    const response = await fetch(`/api/tag/${tagId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete tag' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

/** Every ingredient carrying this tag, alphabetical — the tags explorer's
 *  equivalent of "Receptes on s'utilitza". */
export async function getIngredientsForTag(tagId: string): Promise<Ingredient[]> {
    const response = await fetch(`/api/tag/${tagId}/ingredients`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Every "implies" edge, flat — see lib/tags/tagGraph.ts, which computes
 *  implied tags for display from these rather than the server storing
 *  them. */
export async function listTagImplications(): Promise<TagImplication[]> {
    const response = await fetch('/api/tags/implications');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Replace tagId's direct outgoing implications with exactly this set. */
export async function setTagImplications(tagId: string, impliesTagIds: string[]): Promise<TagImplication[]> {
    const response = await fetch(`/api/tag/${tagId}/implications`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify({ impliesTagIds }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to set tag implications' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Assign a tag to an ingredient by name — creates the tag first if none
 *  by that name exists yet. Returns the updated ingredient. */
export async function addTagToIngredient(ingredientId: string, tagName: string): Promise<Ingredient> {
    const response = await fetch(`/api/ingredient/${ingredientId}/tags`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify({ name: tagName }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to add tag' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Unassign a tag from an ingredient. The tag itself isn't deleted. */
export async function removeTagFromIngredient(ingredientId: string, tagId: string): Promise<Ingredient> {
    const response = await fetch(`/api/ingredient/${ingredientId}/tags/${tagId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to remove tag' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}
